import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Copy, Loader2, ArrowRight } from "lucide-react";

const fmt = (n) => `${(Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const STATI = { pagata: "Pagata", parziale: "Parziale", da_pagare: "Da pagare", non_pagata: "Non pagata" };
const CAMPI = [["imponibile", "Imponibile"], ["iva_importo", "IVA"], ["totale", "Totale"]];

const Versione = ({ titolo, d, altro, cls, testid }) => (
  <div className={`rounded-md border p-2 text-xs space-y-0.5 min-w-0 ${cls}`} data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest font-bold">{titolo}</div>
    <div className="truncate">{d.controparte || "—"}</div>
    {CAMPI.map(([k, l]) => {
      const diff = Math.abs((Number(d[k]) || 0) - (Number(altro[k]) || 0)) > 0.009;
      return <div key={k} className="flex justify-between gap-2"><span className="text-muted-foreground">{l}</span><span className={`tabular-nums ${diff ? "font-extrabold text-amber-700" : "font-semibold"}`}>{fmt(d[k])}</span></div>;
    })}
  </div>
);

const Riga = ({ c, i, onPick }) => {
  const nuova = c.scelta === "nuova";
  const ex = c.existing, nd = c.new_doc;
  return (
    <div className="rounded-lg border border-border p-3 space-y-2" data-testid={`dup-row-${i}`}>
      <div className="text-sm font-semibold">Fattura n. {nd.numero} del {nd.data} <span className="text-xs text-muted-foreground font-normal">· esistente: {STATI[ex.stato] || ex.stato}</span></div>
      <div className="text-xs">Totale {fmt(ex.totale)} <ArrowRight className="inline h-3 w-3" /> <strong className="text-primary">{fmt(nd.totale)}</strong></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Versione titolo="Già presente" d={ex} altro={ex} cls={!nuova ? "border-primary bg-primary/5" : "border-border"} testid={`dup-esistente-${i}`} />
        <Versione titolo="Nuova dal file" d={nd} altro={ex} cls={nuova ? "border-primary bg-primary/5" : "border-border"} testid={`dup-nuova-${i}`} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button variant={!nuova ? "default" : "outline"} className="h-11" onClick={() => onPick("esistente")} data-testid={`dup-tieni-esistente-${i}`}>Tieni quella esistente</Button>
        <Button variant={nuova ? "default" : "outline"} className="h-11" onClick={() => onPick("nuova")} data-testid={`dup-tieni-nuova-${i}`}>Tieni quella nuova</Button>
      </div>
    </div>
  );
};

export const ImportDoppioniDialog = ({ list, imported, onClose, onDone }) => {
  const [righe, setRighe] = useState(() => list.map((c) => ({ ...c, scelta: "nuova" })));
  const [busy, setBusy] = useState(false);
  const [esito, setEsito] = useState(null);
  const tutte = (scelta) => setRighe((r) => r.map((c) => ({ ...c, scelta })));

  const conferma = async () => {
    setBusy(true);
    try {
      const resolutions = righe.map((c) => (c.scelta === "nuova" ? { action: "update_amounts", target_id: c.existing.id, new_doc: c.new_doc } : { action: "skip" }));
      const { data } = await api.post("/fatture/bulk-import/resolve", { resolutions });
      setEsito(data); onDone?.();
      if (data.errors?.length) toast.error(data.errors[0]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="dup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Copy className="h-5 w-5 text-primary" /> {list.length} fatture già presenti</DialogTitle>
          <DialogDescription>Stesso numero e stessa data. Scegli quale versione tenere: con "nuova" si aggiornano solo imponibile, IVA e totale; pagamenti, allegati, cantiere e DDT restano.</DialogDescription>
        </DialogHeader>
        {!esito ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => tutte("esistente")} data-testid="dup-tutte-esistenti">Tieni tutte le esistenti</Button>
              <Button variant="outline" onClick={() => tutte("nuova")} data-testid="dup-tutte-nuove">Tieni tutte le nuove</Button>
            </div>
            {righe.map((c, i) => <Riga key={i} c={c} i={i} onPick={(s) => setRighe((r) => r.map((x, j) => (j === i ? { ...x, scelta: s } : x)))} />)}
            <Button className="w-full h-11 sticky bottom-0" onClick={conferma} disabled={busy} data-testid="dup-conferma">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Conferma scelte</Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="dup-riepilogo">
            <div className="grid grid-cols-3 gap-2 text-center">
              {[["Importate", imported], ["Aggiornate", esito.overwritten], ["Ignorate", esito.skipped]].map(([l, v]) => (
                <div key={l} className="rounded-md bg-secondary p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div><div className="text-2xl font-extrabold" data-testid={`dup-tot-${l.toLowerCase()}`}>{v}</div></div>
              ))}
            </div>
            <Button variant="outline" className="w-full" onClick={onClose} data-testid="dup-chiudi">Chiudi</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
